import { logError, logInfo } from '../shared/utils/logging';
import { parseIgnoreSelectors } from './domUtils';
import { REGISTERED_APPS } from '../shared/types/Apps';
import {
  CandyCartAppInfo,
  CandyRackAppInfo,
} from '../shared/types/CustomWindow';

export default class DocumentListenerUtilsBase {
  eventName: string;
  registeredApp: REGISTERED_APPS;

  constructor(eventName: string, registeredApp: REGISTERED_APPS) {
    this.eventName = eventName;
    this.registeredApp = registeredApp;
    this.getButtonHandler = this.getButtonHandler.bind(this);
  }

  setupDocumentListener() {
    this.getWindowAppInfo().buttonState = 'idle';
    document.addEventListener('click', this.getButtonHandler, true);

    // Note: There is no need to distinguish between CandyCart and CandyRack:
    // If a merchant wants to enable this, they will want to enable it for the whole store
    if (window.CANDYRACK_LISTEN_FOR_TOUCHEND_EVENT) {
      document.addEventListener('touchend', this.getButtonHandler, true);
    }
  }

  selectors(): string {
    throw new Error('Not implemented');
  }

  /*
   ** We have to return the correct window object like this.
   ** Just saving it as an instance variable could cause issues,
   ** as the relevant window object won't get updated correctly.
   */
  getWindowAppInfo(): CandyRackAppInfo | CandyCartAppInfo {
    return this.registeredApp === REGISTERED_APPS.ADD_TO_CART
      ? window.CandyRack
      : window.CandyCart;
  }

  getButtonHandler(event: Event) {
    try {
      let target = this.findTarget(event);
      if (!target) {
        return;
      }
      this.handleEventPropagationForTargetOnClick(target, event);
      this.handleTimeOutsForButtonStates(target);
    } catch (e) {
      logError('ButtonHandler failed', this.registeredApp, e);
    }
  }

  findTarget(event: Event): HTMLElement | null {
    let target = event.target as HTMLElement | null;
    if (!target) {
      logInfo('event.target is null', this.registeredApp, event);
      return null;
    }
    if (this.matchSelectorsDirectly()) {
      // if target matches selectors we have what we looking for.
      if (!target.matches(this.selectors())) {
        // if not keep looking
        target = target.closest(this.selectors());
      }
    } else {
      if (
        !(
          target instanceof HTMLInputElement ||
          target instanceof HTMLButtonElement ||
          target instanceof HTMLDivElement ||
          target instanceof HTMLAnchorElement
        )
      ) {
        target = target.closest('input, a, button, div');
      }
    }

    if (!target) {
      logInfo('target.closest is null', this.registeredApp, event);
      return null;
    }
    logInfo(
      {
        state: this.getWindowAppInfo().buttonState,
        target,
        eventTarget: event.target,
      },
      this.registeredApp,
    );
    return target;
  }

  matchSelectorsDirectly(): boolean | undefined {
    return undefined;
  }

  private handleEventPropagationForTargetOnClick(
    target: HTMLElement,
    event: Event,
  ): void {
    if (
      target.matches(this.selectors()) &&
      this.getWindowAppInfo().buttonState === 'idle'
    ) {
      // Converting selectors to a string as some stores use arrays in custom scripts
      const selectorsToIgnore = parseIgnoreSelectors(
        this.getWindowAppInfo().customIgnoreButtonSelector,
      );
      if (
        selectorsToIgnore &&
        selectorsToIgnore.trim().length &&
        target.matches(selectorsToIgnore)
      ) {
        logInfo(
          'target ignored by customIgnoreButtonSelector',
          this.registeredApp,
          target,
        );
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      logInfo(`Firing ${this.eventName}`, this.registeredApp);

      document.dispatchEvent(
        new CustomEvent(this.eventName, {
          detail: { clonedButton: target, hiddenButton: target },
        }),
      );

      setTimeout(() => {
        if (this.getWindowAppInfo().buttonState === 'idle') {
          this.getWindowAppInfo().buttonState = 'failed';
          target?.click();
        }
      }, 500);
    }
  }

  private handleTimeOutsForButtonStates(target: HTMLElement): void {
    if (
      target.matches(this.selectors()) &&
      this.getWindowAppInfo().buttonState === 'pass-through'
    ) {
      setTimeout(() => {
        this.getWindowAppInfo().buttonState = 'idle';
      }, 3000);
    }
  }
}
